// Rules of the Dispatch (Crew Transfer) page. Trip status groups follow the API's Rides/get-rides (AirlineRides.sql):
// Pending 101 · On job 102, 104, 107, 108, 114 · Completed 109, 110, 116 · Cancelled 111.

export const DUTY_TYPES = [
  { id: 0, name: "All duty" },
  { id: 1, name: "Fly" },
  { id: 2, name: "Non fly" },
];

export const TIME_SLOTS = [
  { id: 0, name: "All time" },
  { id: 1, name: "00:00 - 06:00" },
  { id: 2, name: "06:00 - 12:00" },
  { id: 3, name: "12:00 - 18:00" },
  { id: 4, name: "18:00 - 23:59" },
];

const GROUPS = {
  pending: [101],
  onJob: [102, 104, 107, 108, 114],
  completed: [109, 110, 116],
  cancelled: [111],
};

/** Summary tiles (client side, on the loaded rides); "" = no tile filter. */
export const TILES = [
  { id: "pending", label: "Pending", icon: "fas fa-hourglass-half" },
  { id: "onJob", label: "On job", icon: "fas fa-route" },
  { id: "completed", label: "Completed", icon: "fas fa-check-circle" },
  { id: "cancelled", label: "Cancelled", icon: "fas fa-ban" },
];

export const inGroup = (trip, group) => GROUPS[group]?.includes(trip.tripStatusId) ?? false;

/** Rides per tile, plus the total and the non-fly rides. */
export function countTrips(trips) {
  const count = { total: trips.length, fly: 0, nonFly: 0, pending: 0, onJob: 0, completed: 0, cancelled: 0 };
  trips.forEach((t) => {
    if (t.activityTypeId === 2) count.nonFly += 1;
    if (t.activityTypeId === 0 || t.activityTypeId === 1) count.fly += 1;
    Object.keys(GROUPS).forEach((g) => {
      if (inGroup(t, g)) count[g] += 1;
    });
  });
  return count;
}

/** Fly = activity type 0 / 1, Non fly = 2. */
const dutyMatch = (t, dutyTypeId) => !dutyTypeId || (dutyTypeId === 1 ? t.activityTypeId === 0 || t.activityTypeId === 1 : t.activityTypeId === 2);

/** Pickup time inside the slot (minutes from midnight); rides without a pickup time always match. */
function slotMatch(t, timeSlotId) {
  if (!timeSlotId || !t.pickupTime) return true;
  const d = new Date(t.pickupTime);
  const mins = d.getHours() * 60 + d.getMinutes();
  return [mins < 360, mins >= 360 && mins < 720, mins >= 720 && mins < 1080, mins >= 1080][timeSlotId - 1];
}

/** Clubbing filter: true = only clubbed rides, false = all. */
const clubMatch = (t, club) => !club || Boolean(t.isClubbed);

/** Clubbed rides of the list. */
export const countClub = (trips) => trips.filter((t) => t.isClubbed).length;

/** The rides shown: duty type, time slot, clubbing and the selected tile. */
export const filterTrips = (trips, { dutyTypeId = 0, timeSlotId = 0, club = false, tile = "" }) =>
  trips.filter((t) => dutyMatch(t, dutyTypeId) && slotMatch(t, timeSlotId) && clubMatch(t, club) && (!tile || inGroup(t, tile)));

/** CSS tone of a ride's status (chip + row accent), same colours as before. */
export function statusTone(trip) {
  const id = trip.tripStatusId;
  if (id === 101) return trip.activityTypeId === 2 ? "pending-nonfly" : "pending";
  if (id === 111) return "cancelled";
  if (id === 104) return "enroute";
  if (id === 103 || id === 105) return "unassigned";
  if (id === 107) return "location";
  if (id === 108) return "boarded";
  if (id === 109 || id === 110) return "noshow";
  if (id === 116) return "completed";
  return "other";
}

/** Live tracking only while on the job; replay once complete with location, completion and a vehicle. */
export const canTrack = (trip) => Boolean(trip.isOnJob);
export const canReplay = (trip) => Boolean(trip.tripStatusId === 116 && trip.locationTime && trip.completedTime && trip.vehicleId);

/**
 * Activity / Flight text: ground duty with no activity location = activity code + its flight ("DHFLT QP 1828"), with a
 * location = "code - location", else the ride's activity / flight ("QP 1101").
 */
export function activityText(trip) {
  if (trip.activityTypeId === 2 && !trip.activityLocationCode && trip.activityCode && trip.flightNo) {
    return [trip.activityCode, trip.airlineCode, trip.flightNo].filter(Boolean).join(" ");
  }
  return trip.activityDisplay ?? "";
}

/** Clubbing cell / filter text: "Clubbed · No. 286833" or "Not clubbed". */
export const clubText = (trip) => (trip.isClubbed && trip.clubbedNo ? `Clubbed · No. ${trip.clubbedNo}` : "Not clubbed");

/** Gender filter / search / export text: "FEMALE · Cabin Crew" (crew type alone when the gender is not known). */
export const genderText = (trip) => [trip.gender?.toUpperCase(), trip.crewType].filter(Boolean).join(" · ");

/** Text searched in the mobile cards. */
export const tripSearchText = (t) =>
  [
    t.rideId,
    t.employeeNo,
    t.paxName,
    t.paxMobileNo,
    t.transporterName,
    t.pickupLocation,
    t.dropoffLocation,
    t.vehicleNo,
    t.driverName,
    t.tripStatus,
    activityText(t),
  ]
    .filter(Boolean)
    .join(" ");
